import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarClock, Loader2, ArrowRight, CheckCircle2, Video, X, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api/public` });

const fmtLongDate = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  } catch { return iso; }
};

/* Modal Book a call — 3 pasos:
   1) Selección de día → slot
   2) Formulario (nombre, email, teléfono, nota)
   3) Confirmación con Google Meet
*/
export default function BookCallDialog({ token, consultantName, trigger }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [avail, setAvail] = useState(null);
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const reset = () => {
    setStep(1); setSelectedDay(""); setSelectedSlot("");
    setForm({ name: "", email: "", phone: "", notes: "" });
    setResult(null); setError("");
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    pub.get(`/trip/${token}/consultant-availability`, { params: { days: 14 } })
      .then(({ data }) => setAvail(data))
      .catch(() => setAvail({ connected: false, reason: "error", days: [] }))
      .finally(() => setLoading(false));
  }, [open, token]);

  const daysWithSlots = useMemo(() => (avail?.days || []).filter((d) => d.slots?.length), [avail]);
  const dayObj = useMemo(() => daysWithSlots.find((d) => d.date === selectedDay), [daysWithSlots, selectedDay]);

  const submit = async () => {
    setError("");
    if (!form.name.trim()) { setError("Please tell us your name"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { setError("Please enter a valid email address"); return; }
    setBusy(true);
    try {
      const { data } = await pub.post(`/trip/${token}/book-call`, {
        date: selectedDay, time: selectedSlot, duration_min: 30,
        name: form.name.trim(), email: form.email.trim(),
        phone: form.phone.trim(), notes: form.notes.trim(),
      });
      setResult(data);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || "We couldn't book your call — please try another time.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      {trigger ? React.cloneElement(trigger, { onClick: () => setOpen(true) }) : null}
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto rounded-2xl border-0 bg-white p-0 shadow-2xl"
        data-testid="bookcall-dialog"
      >
        <div className="border-b border-[#F0EDE6] bg-[#F9F8F6] px-6 py-5">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2 font-heading text-xl font-medium text-[#121B28]">
              <CalendarClock className="h-5 w-5 text-[#8a9771]" /> Book a call
            </DialogTitle>
          </DialogHeader>
          <p className="mt-1 text-sm font-light text-[#52525B]">
            {step === 1 && (avail?.connected === false
              ? "Direct booking isn't available yet — please use email or phone."
              : `Pick a time that works for you. ${consultantName ? `${consultantName} will meet you on Google Meet.` : "You'll meet on Google Meet."}`)}
            {step === 2 && "One last thing — share your details so we can send the invite."}
            {step === 3 && "You're all set! Check your inbox for the calendar invite."}
          </p>
        </div>

        {/* Step 1: Slots */}
        {step === 1 && (
          <div className="max-h-[60vh] overflow-y-auto px-6 py-5" data-testid="bookcall-step-slots">
            {loading && <div className="flex items-center justify-center py-10 text-sm text-[#52525B]"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading available times…</div>}
            {!loading && avail?.connected === false && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="bookcall-unavailable">
                Online booking isn't set up yet for this specialist. Please reach out by email or phone below and they'll come back with proposed times.
              </div>
            )}
            {!loading && avail?.connected && daysWithSlots.length === 0 && (
              <p className="py-6 text-center text-sm text-[#52525B]">No free slots in the next 14 days. Please reach out by email or phone.</p>
            )}
            {!loading && avail?.connected && daysWithSlots.length > 0 && (
              <div className="space-y-4">
                {daysWithSlots.map((d) => (
                  <div key={d.date} data-testid={`bookcall-day-${d.date}`}>
                    <p className="et-sans mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8a9771]">{fmtLongDate(d.date)}</p>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {d.slots.map((s) => {
                        const selected = selectedDay === d.date && selectedSlot === s;
                        return (
                          <button
                            key={s}
                            onClick={() => { setSelectedDay(d.date); setSelectedSlot(s); }}
                            className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${selected ? "border-[#121B28] bg-[#121B28] text-white" : "border-[#E4E4E7] bg-white text-[#1A1A1A] hover:border-[#121B28]/50"}`}
                            data-testid={`bookcall-slot-${d.date}-${s}`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="flex items-center justify-end gap-2 border-t border-[#F0EDE6] bg-[#F9F8F6] px-6 py-3">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="bookcall-cancel">Cancel</Button>
            <Button
              onClick={() => setStep(2)}
              disabled={!selectedDay || !selectedSlot}
              className="rounded-full bg-[#121B28] px-5 text-white hover:opacity-90"
              data-testid="bookcall-next"
            >
              Continue <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Step 2: Form */}
        {step === 2 && (
          <div className="px-6 py-5" data-testid="bookcall-step-form">
            <div className="mb-4 rounded-lg bg-[#F4EBD7]/60 px-4 py-3 text-sm text-[#1A1A1A]">
              <p className="font-semibold">{fmtLongDate(selectedDay)}</p>
              <p className="font-light">{selectedSlot} · 30 minutes on Google Meet</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Your name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe" data-testid="bookcall-input-name" autoFocus />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Email address</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com" data-testid="bookcall-input-email" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Phone <span className="font-light normal-case text-[#A1A1AA]">(optional)</span></Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 555 123 456" data-testid="bookcall-input-phone" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">What would you like to discuss? <span className="font-light normal-case text-[#A1A1AA]">(optional)</span></Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3} placeholder="Questions about the itinerary, custom experiences, dietary needs…" data-testid="bookcall-input-notes" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="bookcall-error">{error}</p>}
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="flex items-center justify-between gap-2 border-t border-[#F0EDE6] bg-[#F9F8F6] px-6 py-3">
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="bookcall-back"><ArrowLeft className="mr-1.5 h-4 w-4" />Back</Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="rounded-full bg-[#121B28] px-5 text-white hover:opacity-90"
              data-testid="bookcall-confirm"
            >
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Confirm booking
            </Button>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && result && (
          <div className="px-6 py-8 text-center" data-testid="bookcall-step-success">
            <CheckCircle2 className="mx-auto mb-3 h-14 w-14 text-[#8a9771]" />
            <h3 className="font-heading text-2xl font-medium text-[#1A1A1A]">Your call is confirmed</h3>
            <p className="mx-auto mt-2 max-w-sm text-sm font-light text-[#52525B]">
              We've sent a Google Calendar invite to <strong className="font-semibold text-[#1A1A1A]">{form.email}</strong> with the meeting details.
            </p>
            <div className="mx-auto mt-5 max-w-sm rounded-lg border border-[#E4E4E7] bg-[#F9F8F6] p-4 text-left text-sm">
              <p className="font-semibold text-[#1A1A1A]">{fmtLongDate(selectedDay)}</p>
              <p className="mt-0.5 font-light text-[#52525B]">{selectedSlot} · with {result.consultant_name || consultantName}</p>
              {result.meet_link && (
                <a href={result.meet_link} target="_blank" rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#121B28] px-3 py-1.5 text-xs font-semibold text-[#121B28] transition-colors hover:bg-[#121B28] hover:text-white"
                  data-testid="bookcall-meet-link">
                  <Video className="h-3.5 w-3.5" /> Open Google Meet
                </a>
              )}
            </div>
            <Button onClick={() => setOpen(false)} className="mt-6 rounded-full bg-[#121B28] px-6 text-white hover:opacity-90" data-testid="bookcall-close">
              Done <X className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
